import React, { useState, useEffect } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { CheckCircle, Shield, Clock, Award, Users, TrendingUp } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { Pricing } from '@/components/sections/Pricing';
import { Testimonials } from '@/components/sections/Testimonials';
import { usePopup } from '@/context/PopupContext';
import { getGeoLocation, submitLead } from '@/services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formSchema = z.object({
  name: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must not exceed 50 characters'),
  email: z.string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address (e.g., user@example.com)'),
  country_code: z.string(),
  contact_number: z.string()
    .min(10, 'Phone number must be exactly 10 digits after country code')
    .max(10, 'Phone number must be exactly 10 digits (no more than 10)')
    .regex(/^\d{10}$/, 'Phone number must contain exactly 10 digits only (alphabets and special characters are not allowed)'),
  message: z.string()
    .max(500, 'Message must not exceed 500 characters')
    .optional(),
});

const faqs = [
  {
    question: 'How long does the trademark registration process take?',
    answer: 'The typical trademark registration process takes 8-12 months from filing to final approval. However, you can start using the TM symbol immediately after filing, and we provide 24-hour filing service to get your application submitted quickly.'
  },
  {
    question: 'What services are included in the $45 package?',
    answer: 'Our $45 Essentials package includes a comprehensive trademark search, expert application preparation, direct USPTO filing, and basic monitoring. All our packages come with professional legal guidance and ongoing support throughout the process.'
  },
  {
    question: 'Do I really need to register my trademark?',
    answer: 'Yes! Trademark registration provides nationwide exclusive rights to your brand name and logo, legal protection against infringement, ability to sue for damages, and significantly increases your brand value. It\'s essential for any serious business.'
  },
  {
    question: 'Can you help with other intellectual property services besides trademarks?',
    answer: 'Absolutely! We offer comprehensive IP services including copyright registration for creative works, patent services for inventions, company formation (LLC, C-Corp, S-Corp), Amazon Brand Registry enrollment, and ongoing trademark monitoring and renewal services.'
  },
  {
    question: 'What happens if my trademark application is rejected?',
    answer: 'If your application receives an office action or rejection, our experienced attorneys will prepare and file a comprehensive legal response at no additional cost for our Enhanced and higher packages. We have a 98% success rate in overcoming office actions.'
  }
];

export const LandingPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const { openPopup } = usePopup();
  
  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: { country_code: '+1' }
  });

  // Fetch geolocation data on component mount
  useEffect(() => {
    const fetchGeoData = async () => {
      const data = await getGeoLocation();
      setGeoData(data);
      
      // Auto-select country code based on geolocation
      if (data?.data?.countryCode) {
        const countryCodeMap = {
          'US': '+1', 'GB': '+44', 'IN': '+91', 'AU': '+61', 'CN': '+86',
          'JP': '+81', 'DE': '+49', 'FR': '+33', 'IT': '+39', 'ES': '+34',
          'RU': '+7', 'BR': '+55', 'MX': '+52', 'KR': '+82', 'NL': '+31',
          'SE': '+46', 'CH': '+41', 'NO': '+47', 'DK': '+45', 'PL': '+48',
          'SG': '+65', 'MY': '+60', 'TH': '+66', 'VN': '+84', 'ID': '+62',
          'PH': '+63', 'PK': '+92', 'BD': '+880', 'AE': '+971', 'SA': '+966',
          'ZA': '+27', 'NG': '+234', 'EG': '+20', 'KE': '+254', 'NZ': '+64',
          'IE': '+353', 'PT': '+351', 'GR': '+30', 'BE': '+32', 'AT': '+43', 'FI': '+358'
        };
        const dialCode = countryCodeMap[data.data.countryCode];
        if (dialCode) {
          setValue('country_code', dialCode);
        }
      }
    };
    
    fetchGeoData();
  }, [setValue]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const result = await submitLead(data, geoData);
      
      // Handle 200 Success - Redirect to application page
      if (result.success && result.statusCode === 200 && result.customerId) {
        toast.success('Form submitted successfully! Redirecting...');
        reset();
        // Use window.location.href for full page reload
        window.location.href = `/application/main?customerid=${result.customerId}`;
      } 
      // Handle 401 Unauthorized
      else if (result.statusCode === 401) {
        toast.error(result.message || 'Domain not configured. Please contact support.');
      }
      // Handle 400 Bad Request
      else if (result.statusCode === 400) {
        toast.error(result.message || 'Invalid data provided. Please check your information.');
      }
      // Handle other errors
      else {
        toast.error(result.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Spacer for fixed navbar */}
      <div className="h-[116px]"></div>
      
      {/* Hero Header Section */}
      <section className="relative py-16 md:py-24 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 overflow-hidden">
        {/* Enhanced animated background elements */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-96 h-96 bg-blue-600 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
          <div className="absolute top-40 right-10 w-96 h-96 bg-cyan-600 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-20 left-1/2 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
        </div>

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-950/50 to-transparent"></div>
        
        <div className="container mx-auto px-3 sm:px-4 md:px-6 max-w-7xl relative z-10 w-full overflow-hidden">
          <div className="grid lg:grid-cols-[1.3fr_1fr] gap-3 sm:gap-4 md:gap-8 lg:gap-12 items-center w-full">
            {/* Left Content - Order 2 on mobile, Order 1 on desktop */}
            <div className="text-white order-2 lg:order-1 w-full overflow-hidden">
              <p className="text-blue-400 font-semibold mb-2 md:mb-3 text-[10px] sm:text-xs md:text-sm lg:text-base uppercase tracking-wide flex items-center gap-1 sm:gap-2 overflow-hidden">
                <span className="inline-block w-6 sm:w-8 h-0.5 bg-blue-400 flex-shrink-0"></span>
                <span className="truncate text-[9px] sm:text-[10px] md:text-xs">Protect Your Brand - Fast, Affordable, Reliable</span>
              </p>
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-heading font-bold mb-3 sm:mb-4 md:mb-6 leading-tight">
                <span className="bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent block">
                  Trademark Registration
                </span>
                <span className="text-blue-400 block">Made Simple & Affordable</span>
              </h1>
              <p className="text-xs sm:text-sm md:text-base text-gray-300 mb-4 sm:mb-6 md:mb-8 leading-relaxed">
                Secure nationwide exclusive rights to your brand name and logo. Get started for just $45.
              </p>
              <Button
                onClick={() => openPopup('landing')}
                size="sm"
                className="bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 hover:from-blue-700 hover:via-blue-600 hover:to-cyan-600 text-white font-bold text-xs sm:text-sm md:text-base px-4 sm:px-6 md:px-8 py-2 sm:py-3 md:py-4 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-blue-500/50 pulse-animation w-full sm:w-auto"
              >
                Get Started Now →
              </Button>
            </div>

            {/* Right Form - Order 1 on mobile, Order 2 on desktop */}
            <Card className="bg-gradient-to-br from-slate-900 to-blue-950 border-2 border-blue-500/40 shadow-2xl shadow-blue-500/20 order-1 lg:order-2 hover:border-blue-400/60 transition-all duration-300 max-w-full lg:max-w-md xl:max-w-lg">
              <CardContent className="p-3 sm:p-4 md:p-6 lg:p-6">
                <div className="text-center mb-2 md:mb-4 px-2">
                  <h3 className="text-[11px] sm:text-base md:text-xl lg:text-2xl font-heading font-bold text-white mb-1 leading-tight">
                    Get Started for Only $45
                  </h3>
                  <p className="text-[8px] sm:text-xs md:text-sm text-gray-400 leading-tight">Fill the form and start protecting your brand</p>
                  {geoData?.data?.countryName && (
                    <p className="text-blue-400 text-[8px] sm:text-xs mt-1">
                      📍 {geoData.data.countryName}
                    </p>
                  )}
                </div>
                
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-3 md:space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-gray-300">Name *</Label>
                    <Input {...register('name')} placeholder="Your full name" className="mt-1" />
                    {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name.message}</p>}
                  </div>
                  
                  <div>
                    <Label htmlFor="email" className="text-gray-300">Email *</Label>
                    <Input {...register('email')} type="email" placeholder="your@email.com" className="mt-1" />
                    {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email.message}</p>}
                  </div>
                  
                  <div>
                    <Label className="text-gray-300">Contact Number *</Label>
                    <div className="flex gap-2 mt-1">
                      <Select defaultValue="+1" onValueChange={(value) => setValue('country_code', value)}>
                        <SelectTrigger className="w-28 text-white bg-gray-800 border-gray-700">
                          <SelectValue className="text-white" />
                        </SelectTrigger>
                        <SelectContent position="popper" side="bottom" align="start" className="max-h-60 overflow-auto z-50">
                          <SelectItem value="+1">🇺🇸 +1</SelectItem>
                          <SelectItem value="+44">🇬🇧 +44</SelectItem>
                          <SelectItem value="+91">🇮🇳 +91</SelectItem>
                          <SelectItem value="+61">🇦🇺 +61</SelectItem>
                          <SelectItem value="+86">🇨🇳 +86</SelectItem>
                          <SelectItem value="+81">🇯🇵 +81</SelectItem>
                          <SelectItem value="+49">🇩🇪 +49</SelectItem>
                          <SelectItem value="+33">🇫🇷 +33</SelectItem>
                          <SelectItem value="+39">🇮🇹 +39</SelectItem>
                          <SelectItem value="+34">🇪🇸 +34</SelectItem>
                          <SelectItem value="+7">🇷🇺 +7</SelectItem>
                          <SelectItem value="+55">🇧🇷 +55</SelectItem>
                          <SelectItem value="+52">🇲🇽 +52</SelectItem>
                          <SelectItem value="+82">🇰🇷 +82</SelectItem>
                          <SelectItem value="+31">🇳🇱 +31</SelectItem>
                          <SelectItem value="+46">🇸🇪 +46</SelectItem>
                          <SelectItem value="+41">🇨🇭 +41</SelectItem>
                          <SelectItem value="+47">🇳🇴 +47</SelectItem>
                          <SelectItem value="+45">🇩🇰 +45</SelectItem>
                          <SelectItem value="+48">🇵🇱 +48</SelectItem>
                          <SelectItem value="+65">🇸🇬 +65</SelectItem>
                          <SelectItem value="+60">🇲🇾 +60</SelectItem>
                          <SelectItem value="+66">🇹🇭 +66</SelectItem>
                          <SelectItem value="+84">🇻🇳 +84</SelectItem>
                          <SelectItem value="+62">🇮🇩 +62</SelectItem>
                          <SelectItem value="+63">🇵🇭 +63</SelectItem>
                          <SelectItem value="+92">🇵🇰 +92</SelectItem>
                          <SelectItem value="+880">🇧🇩 +880</SelectItem>
                          <SelectItem value="+971">🇦🇪 +971</SelectItem>
                          <SelectItem value="+966">🇸🇦 +966</SelectItem>
                          <SelectItem value="+27">🇿🇦 +27</SelectItem>
                          <SelectItem value="+234">🇳🇬 +234</SelectItem>
                          <SelectItem value="+20">🇪🇬 +20</SelectItem>
                          <SelectItem value="+254">🇰🇪 +254</SelectItem>
                          <SelectItem value="+64">🇳🇿 +64</SelectItem>
                          <SelectItem value="+353">🇮🇪 +353</SelectItem>
                          <SelectItem value="+351">🇵🇹 +351</SelectItem>
                          <SelectItem value="+30">🇬🇷 +30</SelectItem>
                          <SelectItem value="+32">🇧🇪 +32</SelectItem>
                          <SelectItem value="+43">🇦🇹 +43</SelectItem>
                          <SelectItem value="+358">🇫🇮 +358</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        {...register('contact_number')}
                        type="tel"
                        maxLength={10}
                        placeholder="1234567890"
                        className="flex-1"
                        onKeyPress={(e) => { 
                          if (!/[0-9]/.test(e.key)) e.preventDefault(); 
                        }}
                        onPaste={(e) => {
                          const pastedData = e.clipboardData.getData('text');
                          if (!/^\d+$/.test(pastedData)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </div>
                    {errors.contact_number && <p className="text-xs text-red-400 mt-1">{errors.contact_number.message}</p>}
                  </div>
                  
                  <div>
                    <Label className="text-gray-300">Description (Optional)</Label>
                    <Textarea {...register('message')} placeholder="Tell us about your trademark needs..." rows={3} className="mt-1" />
                    {errors.message && <p className="text-xs text-red-400 mt-1">{errors.message.message}</p>}
                  </div>
                  
                  <Button type="submit" disabled={isSubmitting} className="w-full bg-blue-600 hover:bg-blue-700 py-3 md:py-4 text-sm md:text-base font-semibold">
                    {isSubmitting ? 'Submitting...' : 'Start Your Registration →'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why You Need This Section - Blue Background */}
      <section className="relative py-20 md:py-32 bg-gradient-to-br from-slate-950 via-blue-900 to-slate-900 overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 right-10 w-64 h-64 bg-blue-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-1/4 left-10 w-64 h-64 bg-blue-600 rounded-full filter blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image Left */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-500 rounded-2xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
              <img
                src="https://images.unsplash.com/photo-1763729805496-b5dbf7f00c79?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Trademark registration legal documents"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover border-2 border-blue-500/30 relative z-10 hover:border-blue-400/60 transition-all duration-300"
              />
            </div>
            
            {/* Content Right */}
            <div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6">
                <span className="bg-gradient-to-r from-white via-blue-200 to-white bg-clip-text text-transparent">
                  Why You Need
                </span>
                <br />
                <span className="text-blue-400">Trademark Registration</span>
              </h2>
              <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-8">
                Your brand is your business's most valuable asset. Without trademark registration, anyone can copy your name, logo, or slogan, confusing customers and damaging your reputation. A registered trademark gives you nationwide exclusive rights, legal protection against infringement, and the ability to take legal action if someone tries to steal your brand identity. It also significantly increases your business value and credibility, making it essential for serious entrepreneurs, e-commerce sellers, and established businesses alike.
              </p>
              <Button
                onClick={() => openPopup('landing')}
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-bold text-lg px-8 py-6 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-blue-500/50"
              >
                Register My Trademark →
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work Section - Blue Background (Primary) */}
      <section className="relative py-20 md:py-32 bg-gradient-to-br from-slate-950 via-blue-900 to-slate-900 overflow-hidden">
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-5" style={{backgroundImage: 'linear-gradient(#3b82f6 1px, transparent 1px), linear-gradient(90deg, #3b82f6 1px, transparent 1px)', backgroundSize: '50px 50px'}}></div>
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
              How We Work
            </h2>
            <p className="text-base md:text-lg text-gray-300 max-w-3xl mx-auto">
              We've streamlined the trademark registration process into three simple steps. Our expert team handles everything from initial search to final approval, ensuring your brand receives comprehensive legal protection with minimal effort on your part.
            </p>
          </div>

          {/* Mobile Slider - Only on screens smaller than md */}
          <div className="block md:hidden">
            <Slider
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={3000}
              arrows={false}
            >
              <div className="px-2">
                <Card className="bg-brand-navy-card border border-blue-900/30">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-6">
                      <Users className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
                      Information Collection
                    </h3>
                    <p className="text-sm md:text-base text-gray-300 leading-relaxed">
                      Share your brand details through our simple form. We gather all necessary information about your trademark, including your desired name, logo, and the goods or services you offer.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="px-2">
                <Card className="bg-brand-navy-card border border-blue-900/30">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-6">
                      <Shield className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
                      Research & Search Report
                    </h3>
                    <p className="text-sm md:text-base text-gray-300 leading-relaxed">
                      Our attorneys conduct a comprehensive trademark search across federal, state, and common law databases to ensure your mark is available and won't face conflicts.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="px-2">
                <Card className="bg-brand-navy-card border border-blue-900/30">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-6">
                      <CheckCircle className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
                      Application & Submission
                    </h3>
                    <p className="text-sm md:text-base text-gray-300 leading-relaxed">
                      We prepare your trademark application with precision and file it directly with the USPTO. You'll receive updates at every stage until final approval and registration.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </Slider>
          </div>

          {/* Desktop Grid - Only on md screens and above */}
          <div className="hidden md:grid md:grid-cols-3 gap-8">
            <Card className="bg-gradient-to-br from-slate-900 to-cyan-950 border-2 border-cyan-500/40 transition-all duration-300 hover:border-cyan-400 hover:shadow-[0_0_40px_rgba(6,182,212,0.6)] hover:-translate-y-2 group">
              <CardContent className="p-8 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-600 to-cyan-400 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-cyan-500/50">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
                    Information Collection
                  </h3>
                  <p className="text-sm md:text-base text-gray-300 leading-relaxed">
                    Share your brand details through our simple form. We gather all necessary information about your trademark, including your desired name, logo, and the goods or services you offer.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-slate-900 to-blue-950 border-2 border-blue-500/40 transition-all duration-300 hover:border-blue-400 hover:shadow-[0_0_40px_rgba(59,130,246,0.6)] hover:-translate-y-2 group">
              <CardContent className="p-8 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-400 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-blue-500/50">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
                    Research & Search Report
                  </h3>
                  <p className="text-sm md:text-base text-gray-300 leading-relaxed">
                    Our attorneys conduct a comprehensive trademark search across federal, state, and common law databases to ensure your mark is available and won't face conflicts.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-slate-900 to-cyan-950 border-2 border-cyan-500/40 transition-all duration-300 hover:border-cyan-400 hover:shadow-[0_0_40px_rgba(6,182,212,0.6)] hover:-translate-y-2 group">
              <CardContent className="p-8 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-600 to-cyan-400 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-cyan-500/50">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
                    Application & Submission
                  </h3>
                  <p className="text-sm md:text-base text-gray-300 leading-relaxed">
                    We prepare your trademark application with precision and file it directly with the USPTO. You'll receive updates at every stage until final approval and registration.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <Pricing />

      {/* Why Choose Us Section - Deep Slate with Amber Accents */}
      <section className="relative py-20 md:py-32 bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-amber-600 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-orange-600 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            {/* Content Left */}
            <div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6">
                <span className="bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400 bg-clip-text text-transparent">
                  Why Choose Us
                </span>
              </h2>
              <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                With over a decade of experience and thousands of successful trademark registrations, we've built a reputation for excellence in intellectual property protection. Our team of experienced attorneys, transparent pricing, and industry-leading 98% approval rate make us the trusted choice for entrepreneurs and established businesses nationwide.
              </p>
            </div>

            {/* Image Right */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-600 to-orange-600 rounded-2xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
              <img
                src="https://images.unsplash.com/photo-1758518727613-00192aed759b?q=85&w=800"
                alt="Why choose us"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover border-2 border-amber-500/30 relative z-10 hover:border-amber-400/60 transition-all duration-300"
              />
            </div>
          </div>

          {/* 3 USPs - Mobile Slider */}
          <div className="block md:hidden">
            <Slider
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={3000}
              arrows={false}
            >
              <div className="px-2">
                <Card className="bg-brand-navy-card border border-blue-900/30">
                  <CardContent className="p-8">
                    <Clock className="w-12 h-12 text-blue-400 mb-4" />
                    <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
                      Lightning-Fast 24-Hour Filing
                    </h3>
                    <p className="text-sm md:text-base text-gray-300 leading-relaxed">
                      We understand time is critical for your business. Our expedited processing ensures your trademark application is filed with the USPTO within 24 hours of approval, securing your earliest possible filing date and priority rights.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="px-2">
                <Card className="bg-brand-navy-card border border-blue-900/30">
                  <CardContent className="p-8">
                    <Award className="w-12 h-12 text-blue-400 mb-4" />
                    <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
                      Industry-Leading 98% Success Rate
                    </h3>
                    <p className="text-sm md:text-base text-gray-300 leading-relaxed">
                      Our expert trademark attorneys have a proven track record of success. With comprehensive searches, meticulous application preparation, and strategic office action responses, we achieve a 98% approval rate—significantly higher than industry average.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="px-2">
                <Card className="bg-brand-navy-card border border-blue-900/30">
                  <CardContent className="p-8">
                    <TrendingUp className="w-12 h-12 text-blue-400 mb-4" />
                    <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
                      Comprehensive Legal Protection
                    </h3>
                    <p className="text-sm md:text-base text-gray-300 leading-relaxed">
                      Beyond just filing, we provide ongoing monitoring, renewal reminders, and enforcement support. Our comprehensive service ensures your trademark remains protected throughout its lifetime, giving you complete peace of mind.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </Slider>
          </div>

          {/* 3 USPs - Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-8">
            <Card className="bg-gradient-to-br from-blue-950 to-slate-900 border-2 border-blue-500/40 transition-all duration-300 hover:border-blue-400 hover:shadow-[0_0_40px_rgba(59,130,246,0.6)] hover:-translate-y-2 group">
              <CardContent className="p-8 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <Clock className="w-12 h-12 text-blue-400 mb-4 group-hover:scale-110 transition-transform duration-300" />
                  <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
                    Lightning-Fast 24-Hour Filing
                  </h3>
                  <p className="text-sm md:text-base text-gray-300 leading-relaxed">
                    We understand time is critical for your business. Our expedited processing ensures your trademark application is filed with the USPTO within 24 hours of approval, securing your earliest possible filing date and priority rights.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-950 to-slate-900 border-2 border-blue-500/40 transition-all duration-300 hover:border-blue-400 hover:shadow-[0_0_40px_rgba(59,130,246,0.6)] hover:-translate-y-2 group">
              <CardContent className="p-8 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <Award className="w-12 h-12 text-blue-400 mb-4 group-hover:scale-110 transition-transform duration-300" />
                  <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
                    Industry-Leading 98% Success Rate
                  </h3>
                  <p className="text-sm md:text-base text-gray-300 leading-relaxed">
                    Our expert trademark attorneys have a proven track record of success. With comprehensive searches, meticulous application preparation, and strategic office action responses, we achieve a 98% approval rate—significantly higher than industry average.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-cyan-950 to-slate-900 border-2 border-cyan-500/40 transition-all duration-300 hover:border-cyan-400 hover:shadow-[0_0_40px_rgba(6,182,212,0.6)] hover:-translate-y-2 group">
              <CardContent className="p-8 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <Shield className="w-12 h-12 text-cyan-400 mb-4 group-hover:scale-110 transition-transform duration-300" />
                  <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
                    Comprehensive Legal Protection
                  </h3>
                  <p className="text-sm md:text-base text-gray-300 leading-relaxed">
                    Beyond just filing, we provide ongoing monitoring, renewal reminders, and enforcement support. Our comprehensive service ensures your trademark remains protected throughout its lifetime, giving you complete peace of mind.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Company Stats Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/2 left-1/3 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl animate-blob"></div>
          <div className="absolute top-1/3 right-1/3 w-96 h-96 bg-cyan-500 rounded-full filter blur-3xl animate-blob animation-delay-2000"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-4">
              <span className="bg-gradient-to-r from-white via-blue-200 to-white bg-clip-text text-transparent">
                Trusted by Thousands of Businesses
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-400 rounded-full mb-6 shadow-lg shadow-blue-500/50 group-hover:scale-110 transition-transform duration-300">
                <TrendingUp className="w-10 h-10 text-white" />
              </div>
              <div className="text-5xl font-heading font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-4">15,000+</div>
              <h3 className="text-2xl md:text-3xl font-semibold text-white mb-3">Trademarks Registered</h3>
              <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                Successfully registered over 15,000 trademarks across all industries, from tech startups to established retail brands, providing comprehensive brand protection nationwide.
              </p>
            </div>

            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-400 rounded-full mb-6 shadow-lg shadow-blue-500/50 group-hover:scale-110 transition-transform duration-300">
                <Users className="w-10 h-10 text-white" />
              </div>
              <div className="text-5xl font-heading font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-4">98%</div>
              <h3 className="text-2xl md:text-3xl font-semibold text-white mb-3">Client Satisfaction Rate</h3>
              <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                Our clients consistently rate us 5 stars for our professional service, clear communication, and successful outcomes. We're committed to exceeding expectations at every step.
              </p>
            </div>

            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-cyan-600 to-cyan-400 rounded-full mb-6 shadow-lg shadow-cyan-500/50 group-hover:scale-110 transition-transform duration-300">
                <Clock className="w-10 h-10 text-white" />
              </div>
              <div className="text-5xl font-heading font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent mb-4">10+</div>
              <h3 className="text-2xl md:text-3xl font-semibold text-white mb-3">Years of Experience</h3>
              <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                Over a decade of specialized expertise in trademark law and intellectual property protection. Our seasoned attorneys bring deep knowledge and proven strategies to every case.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section with Background Image */}
      <section
        className="py-24 md:py-32 relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1521791136064-7986c2920216?q=85&w=1920)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/95 to-brand-navy/85" />
        <div className="container mx-auto px-4 md:px-6 max-w-4xl relative z-10 text-center text-white">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6">
            Ready to Protect Your Brand?
          </h2>
          <p className="text-base md:text-lg mb-10 text-gray-200 leading-relaxed">
            Don't wait until someone copies your brand. Secure your trademark today and gain nationwide exclusive rights, legal protection, and peace of mind. Get started for just $45 with our expert team.
          </p>
          <Button
            onClick={() => openPopup('landing')}
            size="lg"
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm sm:text-base md:text-lg px-6 sm:px-8 md:px-12 py-5 sm:py-6 md:py-7 transition-transform hover:scale-105 shadow-2xl w-full sm:w-auto max-w-full"
          >
            <span className="truncate">Start Your Registration Now →</span>
          </Button>
        </div>
      </section>

      {/* Testimonials Section */}
      <Testimonials />

      {/* FAQs Section */}
      <section className="py-20 md:py-32 bg-brand-navy-light">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-base md:text-lg text-gray-300">
              Get answers to common questions about trademark registration and our services
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, idx) => (
              <AccordionItem key={idx} value={`item-${idx}`} className="border border-blue-900/30 rounded-lg px-6 bg-brand-navy-card">
                <AccordionTrigger className="text-left font-semibold text-white hover:text-blue-400">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-300 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Footer Section */}
      <footer className="bg-brand-navy">
        {/* Main Footer Content */}
        <div className="py-16 md:py-20 border-t border-blue-900/30">
          <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
            {/* Logo */}
            <div className="mb-8 flex justify-center">
              <img src="/logo-white.png" alt="US Brand Experts" className="h-16 md:h-20 w-auto" />
            </div>

            {/* Heading */}
            <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              Get started on your trademark protection today.
            </h3>

            {/* Phone Number */}
            <div className="text-2xl md:text-3xl text-gray-300 mb-4">
              Call us at{' '}
              <a href="tel:+18776380328" className="text-blue-400 hover:text-blue-300 transition-colors font-semibold">
                +1 (877) 638-0328
              </a>
            </div>
          </div>
        </div>

        {/* Blue Copyright Bar */}
        <div className="bg-blue-600 py-4">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-white text-sm">
              <p className="text-center md:text-left">
                © Copyright 2026 US Brand Experts. All Rights Reserved
              </p>
              <div className="flex gap-4">
                <a href="/terms-of-service" className="hover:underline transition-colors">
                  Terms & Conditions
                </a>
                <span>|</span>
                <a href="/privacy-policy" className="hover:underline transition-colors">
                  Privacy Policy
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
